/**
 * SitePickerFromGoogle.js
 *
 * A component that helps users pick their school or organization after selecting an address from Google.
 * It checks the local site database for existing matches based on the selected address.
 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useConnect } from '@lore/query-connect';
import { PayloadStates } from '@lore/utils';
import SitePossibleMatchesCard from './SitePossibleMatchesCard';
import titleCase from '@/utils/titleCase';

SitePickerFromGoogle.propTypes = {
  siteData: PropTypes.object.isRequired,
  onSelectSite: PropTypes.func.isRequired,
  onNone: PropTypes.func.isRequired,
};

/**
 * Component for picking a site after an address search.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element} The rendered site picker.
 */
function SitePickerFromGoogle(props) {
  const { siteData, onSelectSite, onNone } = props;
  const nameTerm = siteData.name || '';
  const addressTerm = (siteData.addressLine1 || '').split(',')[0].trim();
  const addressTermShort = (siteData.addressLine1Short || '').split(',')[0].trim();

  // Search for sites in the database matching the selected Google address
  const sites = useConnect('site.find', {
    where: {
      eager: {
        $where: {
          $or: [
            { name: { $ilike: nameTerm ? `%${nameTerm}%` : '%NOMATCH%' } },
            { addressLine1: { $ilike: nameTerm ? `%${nameTerm}%` : '%NOMATCH%' } },
            { addressLine1: { $ilike: addressTerm ? `%${addressTerm}%` : '%NOMATCH%' } },
            { addressLine1: { $ilike: addressTermShort ? `%${addressTermShort}%` : '%NOMATCH%' } },
          ],
        },
      },
    },
    pagination: {
      pageSize: 5,
      order: 'name asc',
    },
  }, {
    enabled: Boolean(nameTerm || addressTerm || addressTermShort)
  });

  const isFetching = sites.state === PayloadStates.FETCHING;
  const hasMatches = sites.state === PayloadStates.RESOLVED && sites.data.length > 0;

  useEffect(() => {
    if (sites.state === PayloadStates.RESOLVED && sites.data.length === 0) {
      onNone();
    }
  }, [sites.state, sites.data.length, onNone]);

  return (
    <div className="flex flex-col gap-4">
      {/* Selected address summary */}
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-gray-900">Selected address</p>
        <p className="text-base text-gray-800">
          {[titleCase(siteData.addressLine1 || ''), siteData.city ? titleCase(siteData.city) : '', siteData.state].filter(item => Boolean(item)).join(', ')}
        </p>
      </div>

      {/* Possible matches card */}
      <SitePossibleMatchesCard
        isFetching={isFetching}
        hasMatches={hasMatches}
        sites={sites}
        onSelectSite={onSelectSite}
        onNone={onNone}
      />
    </div>
  );
}

export default SitePickerFromGoogle;
